import { useState, type ReactNode } from 'react';
import { Check, History } from 'lucide-react';
import clsx from 'clsx';
import type { ReferencePhotoView } from '@/utils/imageHelpers';

interface Props {
  /**
   * The pool of pictures the client sent, already annotated by `partitionReferences`
   * (most-recent-first, with used/earlier photos flagged). This component is purely
   * presentational — it doesn't compute which photos are "used".
   */
  photos: ReferencePhotoView[];
  /** How many photos are hidden from the default view (used on earlier bookings). */
  hiddenCount: number;
  /** Currently-selected URLs (controlled). */
  selected: string[];
  onChange: (urls: string[]) => void;
  label?: string;
  emptyText?: string;
  testid?: string;
  size?: 'thumb' | 'medium';
  /**
   * Extra control for the header row, left of Select all - e.g. the brief's
   * Upload button, which has to stay reachable even when the pool is empty.
   */
  action?: ReactNode;
}

const SIZE_PX: Record<NonNullable<Props['size']>, string> = {
  thumb: 'h-14 w-14',
  medium: 'h-20 w-20',
};

/**
 * Pick the relevant references from everything the client sent. By default only
 * the suggested (unused, most-recent) photos show; "See all photos" reveals the
 * ones already used on earlier bookings, each flagged with a "used on…" badge.
 * Tapping a tile toggles it; the chosen subset is what lands on
 * `booking.referencePhotos` and surfaces across every other booking view.
 */
export default function ReferencePicker({
  photos,
  hiddenCount,
  selected,
  onChange,
  label = 'References',
  emptyText,
  testid,
  size = 'thumb',
  action,
}: Props) {
  const [showAll, setShowAll] = useState(false);
  const selectedSet = new Set(selected);

  const visible = showAll ? photos : photos.filter((p) => !p.hiddenByDefault);
  const allVisibleSelected = visible.length > 0 && visible.every((p) => selectedSet.has(p.url));

  const toggle = (url: string) => {
    onChange(
      selectedSet.has(url) ? selected.filter((u) => u !== url) : [...selected, url],
    );
  };

  const toggleAll = () =>
    onChange(
      allVisibleSelected
        ? selected.filter((u) => !visible.some((p) => p.url === u))
        : [...selected, ...visible.filter((p) => !selectedSet.has(p.url)).map((p) => p.url)],
    );

  // An empty pool still renders its header when the caller supplied an action
  // (the brief's Upload has to stay reachable with nothing on file yet).
  if (photos.length === 0 && !emptyText && !action) return null;

  const header = (
    <div className="mb-2 flex items-center justify-between gap-2">
      <p className="label-small-caps text-gold-bright">
        {label}
        {selected.length > 0 ? (
          <span className="ml-2 font-inter text-[10px] uppercase tracking-widest text-cream-soft">
            {selected.length} selected
          </span>
        ) : null}
      </p>
      <div className="flex items-center gap-2 shrink-0">
        {action}
        {action && visible.length > 0 ? (
          <span className="h-3 w-px bg-gold-dim/40" aria-hidden="true" />
        ) : null}
        {visible.length > 0 ? (
          <button
            type="button"
            onClick={toggleAll}
            className="font-inter text-[10px] uppercase tracking-widest text-cream-faint hover:text-gold-bright transition"
            data-testid={testid ? `${testid}-toggle-all` : undefined}
          >
            {allVisibleSelected ? 'Clear' : 'Select all'}
          </button>
        ) : null}
      </div>
    </div>
  );

  if (photos.length === 0) {
    return (
      <div data-testid={testid}>
        {header}
        {emptyText ? (
          <p className="font-serif text-[12px] text-cream-soft italic">{emptyText}</p>
        ) : null}
      </div>
    );
  }

  return (
    <div data-testid={testid}>
      {header}

      {visible.length > 0 ? (
        <div className="flex flex-wrap gap-1.5">
          {visible.map((photo, i) => {
            const isSelected = selectedSet.has(photo.url);
            return (
              <button
                type="button"
                key={`${photo.url}-${i}`}
                onClick={() => toggle(photo.url)}
                aria-pressed={isSelected}
                aria-label={
                  photo.usedOnLabel
                    ? `${isSelected ? 'Remove reference' : 'Add as reference'} — used on ${photo.usedOnLabel}`
                    : isSelected
                      ? 'Remove reference'
                      : 'Add as reference'
                }
                title={photo.usedOnLabel ? `Used on ${photo.usedOnLabel}` : undefined}
                className={clsx(
                  'group relative shrink-0 overflow-hidden rounded-md border bg-ink-black/50 transition',
                  SIZE_PX[size],
                  isSelected
                    ? 'border-gold-bright ring-2 ring-gold-bright/70 shadow-gold-glow'
                    : 'border-gold-dim/30 opacity-70 hover:opacity-100 hover:border-gold-bright/60',
                )}
                data-testid={testid ? `${testid}-photo-${i}` : undefined}
              >
                <img
                  src={photo.url}
                  alt={photo.caption ?? 'reference'}
                  loading="lazy"
                  className="h-full w-full object-cover"
                />
                {isSelected ? (
                  <span className="absolute top-0.5 right-0.5 flex h-4 w-4 items-center justify-center rounded-full bg-gold-bright text-ink-black shadow">
                    <Check size={11} strokeWidth={3} />
                  </span>
                ) : (
                  <span className="absolute inset-0 bg-ink-black/40 group-hover:bg-transparent transition" />
                )}
                {photo.usedOnLabel ? (
                  <span
                    className="absolute inset-x-0 bottom-0 flex items-center gap-0.5 bg-ink-black/75 px-1 py-0.5 font-inter text-[8px] uppercase tracking-wide text-cream-soft"
                    data-testid={testid ? `${testid}-used-badge-${i}` : undefined}
                  >
                    <History size={8} className="shrink-0" />
                    <span className="truncate">{photo.usedOnLabel}</span>
                  </span>
                ) : null}
              </button>
            );
          })}
        </div>
      ) : (
        <p className="font-serif text-[12px] text-cream-soft italic">
          Every photo on file was used on an earlier booking.
        </p>
      )}

      {hiddenCount > 0 ? (
        <div className="mt-2 flex items-center justify-between">
          <span className="font-inter text-[10px] uppercase tracking-widest text-cream-faint">
            {showAll ? `${hiddenCount} used on earlier bookings` : `${hiddenCount} earlier/used photos hidden`}
          </span>
          <button
            type="button"
            onClick={() => setShowAll((v) => !v)}
            className="font-inter text-[10px] uppercase tracking-widest text-gold-bright hover:text-gold-bright/80 transition"
            data-testid={testid ? `${testid}-toggle-hidden` : undefined}
          >
            {showAll ? 'Show suggested only' : 'See all photos'}
          </button>
        </div>
      ) : null}
    </div>
  );
}
